<script>
    import * as Sentry from '@sentry/vue';
    import { deviceType } from 'detect-it';
    import { isFunction } from 'lodash-es';
    import { cloneVNode } from 'vue';
    import TooltipException from '@/exceptions/TooltipException.js';

    export default {
        name: 'ActivixTooltip',

        compatConfig: {
            RENDER_FUNCTION: false,
            INSTANCE_ATTRS_CLASS_STYLE: false,
        },

        // Prevent attribute fallthrough: in Vue 3 unrecognized attrs (e.g. `:disabled` used to
        // turn the tooltip itself off) would otherwise be applied to the slot child component,
        // mutating its real `disabled` prop. We re-render the slot child verbatim, so any extra
        // attrs would land on the wrong target.
        inheritAttrs: false,

        props: {
            content: {
                type: [String, Function],
                default: '',
            },
            placement: {
                type: String,
                default: 'top',
            },
            disabled: {
                type: Boolean,
                default: false,
            },
            onlyOnTruncate: {
                type: Boolean,
                default: false,
            },
            onlyOnVerticalTruncate: {
                type: Boolean,
                default: false,
            },
            showOnMobile: {
                type: Boolean,
                default: false,
            },
        },

        data() {
            return {
                instance: null,
            };
        },

        computed: {
            // Wrapper for global deviceType to make it easier to mock in tests
            deviceType() {
                return deviceType;
            },
        },

        methods: {
            async onMouseEnter() {
                if (this.disabled) {
                    return;
                }

                if (this.instance) {
                    this.instance.setContent(await this.getContent());
                } else {
                    await this.init(true);
                }

                this.$nextTick(async () => {
                    const content = await this.getContent();
                    const hasNonEmptyContent = content && content.trim() !== '';
                    if (hasNonEmptyContent && this.instance) {
                        this.instance.show();
                    }
                });
            },

            onMouseLeave() {
                if (this.instance) {
                    this.instance.hide();
                }
            },

            onMobileClick() {
                const isMobile = this.lgLayout || this.deviceType === 'touchOnly';
                if (!this.showOnMobile || !isMobile) {
                    return;
                }

                this.onMouseEnter();
            },

            async getContent() {
                if (isFunction(this.content)) {
                    return this.content();
                }

                return this.content ? this.content.trim() : '';
            },

            isOnlyOnTruncate() {
                if (!this.$el) {
                    return true;
                }
                return this.onlyOnTruncate && !(this.$el.offsetWidth < this.$el.scrollWidth);
            },

            isOnlyOnVerticalTruncate() {
                if (!this.$el) {
                    return true;
                }
                return (
                    this.onlyOnVerticalTruncate && !(this.$el.offsetHeight < this.$el.scrollHeight)
                );
            },

            async init(forceRender = false) {
                const isMobile = this.lgLayout || this.deviceType === 'touchOnly';
                if (!this.showOnMobile && isMobile) {
                    return;
                }

                if (!forceRender) {
                    return;
                }

                if (this.isOnlyOnTruncate() && this.isOnlyOnVerticalTruncate()) {
                    return;
                }

                const content = await this.getContent();

                if (!content) {
                    return;
                }

                this.destroy();

                try {
                    this.instance = this.$tooltip.init(this.$el, {
                        content,
                        placement: this.placement,
                        trigger: 'manual',
                    });
                } catch (e) {
                    if (!(e instanceof TooltipException)) {
                        throw e;
                    }

                    Sentry.withScope((scope) => {
                        scope.setTag('tooltip.content', e.tooltipOptions.content);
                        scope.setExtra('options', e.tooltipOptions);
                        scope.setExtra('possibleCause', e.possibleCause);
                        Sentry.captureException(e);
                    });
                }
            },

            destroy() {
                if (!this.instance) {
                    return;
                }

                if (typeof this.instance.destroy === 'function') {
                    this.instance.destroy(true);
                    this.instance = null;
                }
            },
        },

        async mounted() {
            await this.$nextTick();

            this.$el.addEventListener('mouseenter', this.onMouseEnter);
            this.$el.addEventListener('mouseleave', this.onMouseLeave);
            this.$el.addEventListener('focusin', this.onMouseEnter);
            this.$el.addEventListener('focusout', this.onMouseLeave);
            this.$el.addEventListener('click', this.onMobileClick, true);
        },

        beforeUnmount() {
            this.destroy();

            this.$el.removeEventListener('mouseenter', this.onMouseEnter);
            this.$el.removeEventListener('mouseleave', this.onMouseLeave);
            this.$el.removeEventListener('focusin', this.onMouseEnter);
            this.$el.removeEventListener('focusout', this.onMouseLeave);
            this.$el.removeEventListener('click', this.onMobileClick, true);
        },

        render() {
            const children = this.$slots.default?.();

            // Return a single VNode so this.$el points to a real DOM element
            // (needed for mouseenter/mouseleave listeners in mounted).
            // Merge $attrs onto the child so class/style/aria fallthrough still works
            // for callers that rely on it (e.g. Element Plus's ElPopperTrigger).
            if (children?.length === 1) {
                return cloneVNode(children[0], this.$attrs);
            }

            return children;
        },
    };
</script>
